import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import ResumeRateLimitNotice from '../../components/Resume/ResumeRateLimitNotice';
import {
  AUTH_CHANGED_EVENT,
  PROFILE_UPDATED_EVENT,
  fetchMe,
  getCachedSessionUser,
  getStoredToken,
} from '../../services/authService';
import { useAuthModal } from '../../context/AuthModalContext';
import './ResumeHub.css';

const ResumeHub = () => {
  const location = useLocation();
  const { openLoginModal } = useAuthModal();
  const [rateLimitError, setRateLimitError] = useState(null);
  const tokenPresent = !!getStoredToken();
  const [adminState, setAdminState] = useState(() => {
    const cached = getCachedSessionUser();
    const isAdmin = cached?.role === 'ADMIN';
    return {
      loading: tokenPresent && !cached?.role,
      isAdmin,
    };
  });
  const base = '/resume';

  useEffect(() => {
    let cancelled = false;

    const sync = () => {
      if (!getStoredToken()) {
        setAdminState({ loading: false, isAdmin: false });
        return;
      }
      const cached = getCachedSessionUser();
      if (cached?.role) {
        setAdminState({ loading: false, isAdmin: cached.role === 'ADMIN' });
      } else {
        setAdminState({ loading: true, isAdmin: false });
      }
      fetchMe()
        .then((me) => {
          if (!cancelled) {
            setAdminState({ loading: false, isAdmin: me?.role === 'ADMIN' });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setAdminState({
              loading: false,
              isAdmin: getCachedSessionUser()?.role === 'ADMIN',
            });
          }
        });
    };

    sync();
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    window.addEventListener(PROFILE_UPDATED_EVENT, sync);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_CHANGED_EVENT, sync);
      window.removeEventListener(PROFILE_UPDATED_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    setRateLimitError(null);
  }, [location.pathname]);

  const tabs = useMemo(() => {
    return [
      { path: `${base}/contractor`, label: 'Contractor' },
      { path: `${base}/jd-improve`, label: 'JD match' },
      { path: `${base}/fulltime`, label: 'Full-time' },
      ...(adminState.isAdmin ? [{ path: `${base}/fulltime-coach`, label: 'Full-time Coach' }] : []),
    ];
  }, [adminState.isAdmin]);

  return (
    <div className="resume-hub">
      <header className="resume-hub-header">
        <h1 className="resume-hub-header__title">Resume</h1>
      </header>
      <nav className="resume-hub-tabs" aria-label="Resume tools">
        {tabs.map((t) => {
          const active = location.pathname === t.path;
          const needsAuth = t.path.endsWith('/fulltime') && !tokenPresent;
          const tabClass = `resume-hub-tab${active ? ' resume-hub-tab--active' : ''}`;
          if (needsAuth) {
            return (
              <button
                key={t.path}
                type="button"
                className={tabClass}
                aria-current={undefined}
                onClick={() => openLoginModal(t.path)}
              >
                {t.label}
              </button>
            );
          }
          return (
            <Link
              key={t.path}
              to={t.path}
              className={tabClass}
              aria-current={active ? 'page' : undefined}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>

      <ResumeRateLimitNotice limitError={rateLimitError} />

      <div className="resume-hub-content">
        <Outlet context={{ setRateLimitError }} />
      </div>
    </div>
  );
};

export default ResumeHub;
